import React, { useEffect } from 'react';
import Skeleton from '@atlaskit/skeleton';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { setInteractionError } from '@atlaskit/react-ufo/set-interaction-error';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import ErrorState from '@jira/platform__common-components-error-state/src/index.tsx';
import { investigationPageLoadUfo } from './investigationPageLoadUfo.tsx';
import { useTrackAIInteractionDismissOnTabClose } from './useTrackAIInteractionDismissOnTabClose.tsx';

const errorHeader = defineMessage({
	id: 'servicedesk.rca-investigation.error-header.no-translate',
	defaultMessage: "We couldn't load the investigation",
	description: 'Header of error message shown when the RCA investigation page fails to load.',
});

const errorDescription = defineMessage({
	id: 'servicedesk.rca-investigation.error-description.no-translate',
	defaultMessage: 'Something went wrong. Give it a few seconds, then try again.',
	description: 'Description of error message shown when the RCA investigation page fails to load.',
});

const DEFAULT_AI_ERROR_MESSAGE = 'Investigation could not be loaded';

export type PageFallbackErrorProps = {
	onRefresh: () => void;
	error: unknown;
};

const ErrorPage = ({ onRefresh, error }: PageFallbackErrorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { trackAIResultError } = useAIEventsInstrumentation();

	useEffect(() => {
		const aiErrorMessage = error instanceof Error ? error.message : DEFAULT_AI_ERROR_MESSAGE;
		const aiErrorCode = error instanceof FetchError ? error.statusCode : undefined;
		trackAIResultError({ aiErrorCode, aiErrorMessage });
	}, [error, trackAIResultError]);

	useEffect(() => {
		if (error && error instanceof Error) {
			setInteractionError(investigationPageLoadUfo.experienceName, {
				name: error.name,
				errorMessage: error.message,
			});
		}
	}, [error]);

	return (
		<ErrorState
			onRetry={onRefresh}
			header={formatMessage(errorHeader)}
			description={formatMessage(errorDescription)}
		/>
	);
};

const loadingStyles = cssMap({
	root: {
		paddingInline: token('space.300'),
		paddingBlock: token('space.200'),
	},
	graphSkeleton: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		height: '720px',
	},
});

const BasePageSkeleton = () => (
	<UFOLoadHold name="jsm.rca-investigation.page-loading">
		<Stack space="space.300">
			<Stack space="space.100">
				<Skeleton width="140px" height="16px" isShimmering />
				<Skeleton width="280px" height="28px" isShimmering />
			</Stack>
			<Inline space="space.200">
				<Skeleton width="200px" height="80px" borderRadius={token('radius.large')} isShimmering />
				<Skeleton width="200px" height="80px" borderRadius={token('radius.large')} isShimmering />
			</Inline>
			<Stack space="space.200">
				<Skeleton width="120px" height="16px" isShimmering />
				<Skeleton width="100%" height="48px" borderRadius={token('radius.large')} isShimmering />
			</Stack>
			<Stack space="space.200">
				<Skeleton width="160px" height="16px" isShimmering />
				<Skeleton width="100%" height="120px" borderRadius={token('radius.large')} isShimmering />
			</Stack>
			<Stack space="space.100">
				<Skeleton width="140px" height="16px" isShimmering />
				<Box xcss={loadingStyles.graphSkeleton} />
			</Stack>
		</Stack>
	</UFOLoadHold>
);

const LoadingPage = (): React.JSX.Element => {
	useTrackAIInteractionDismissOnTabClose();

	return <BasePageSkeleton />;
};

export const RcaInvestigationPageSkeleton = (): React.JSX.Element => (
	<Box xcss={loadingStyles.root}>
		<BasePageSkeleton />
	</Box>
);

export const PageFallback: {
	Loading: () => React.JSX.Element;
	Error: ({ onRefresh, error }: PageFallbackErrorProps) => React.JSX.Element;
} = {
	Loading: LoadingPage,
	Error: ErrorPage,
};
